import React, { useState } from 'react';
import { Card, SectionTitle, Row, Btn, Chip, ProgressBar, Toggle, EmptyState } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { CHART_DATA } from '../data';

// ── MY RECEIPTS ───────────────────────────────────────────────────
export function MyReceipts({ t, receipts }) {
  const newReceipts = receipts.filter(r => r.isNew);
  const current = receipts.filter(r => !r.archived);
  const archived = receipts.filter(r => r.archived);

  return (
    <div style={{ padding: '16px 16px 32px' }}>
      {/* Auto-email notice */}
      {newReceipts.length > 0 && (
        <div style={{ background: '#2E7D32', borderRadius: 14, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 22 }}>✅</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>New receipt generated!</div>
            <div style={{ fontSize: 12, color: '#A5D6A7', fontWeight: 600, marginTop: 2 }}>PDF receipt auto-emailed to rasel@gmail.com on approval</div>
          </div>
        </div>
      )}
      <div style={{ background: t.primaryLight, borderRadius: 12, padding: '12px 14px', marginBottom: 16, border: `0.5px solid ${t.borderLight}`, fontSize: 12, color: t.primary, fontWeight: 600, lineHeight: 1.6 }}>
        📧 Every receipt is automatically emailed to your registered email when admin approves your payment.
      </div>

      {current.length > 0 && (
        <Card t={t} style={{ marginBottom: 14 }}>
          <SectionTitle t={t} label="Current receipts — P02" right={<div style={{ background: t.primary, color: '#fff', borderRadius: 7, padding: '3px 9px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>Select all</div>} />
          {current.map(r => (
            <div key={r.id} style={{ background: r.isNew ? t.primaryLight : t.surface2, borderRadius: 10, padding: '12px 14px', marginBottom: 10, border: `0.5px solid ${r.isNew ? t.borderLight : t.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 800, color: t.primary }}>{r.id}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {r.isNew && <div style={{ background: '#FDD835', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 800, color: '#1B5E20' }}>🆕 New</div>}
                  <Chip label="✓ Approved" color="#2E7D32" bg="#E8F5E9" border="#A5D6A7" />
                </div>
              </div>
              <Row t={t} label="Months" value={r.months} />
              <Row t={t} label="Amount" value={`${r.amount.toLocaleString()} BDT`} valueColor={t.primary} />
              <Row t={t} last label="Approved" value={r.date} />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Btn t={t} label="↓ Download PDF" style={{ flex: 1, padding: '9px 0', fontSize: 12 }} />
                <Btn t={t} label="🔍 Verify" bg={t.primaryLight} color={t.primary} style={{ flex: 1, padding: '9px 0', fontSize: 12, border: `0.5px solid ${t.borderLight}` }} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {archived.length > 0 && (
        <Card t={t}>
          <SectionTitle t={t} label="Archived receipts (1yr+)" />
          {archived.map(r => (
            <div key={r.id} style={{ background: t.surface2, borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: `0.5px solid ${t.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 800, color: '#F57C00' }}>{r.id}</div>
                <Chip label="📦 Archived" color="#F57C00" bg="#FFF8E1" border="#FFE082" />
              </div>
              <Row t={t} label="Amount" value={`${r.amount.toLocaleString()} BDT`} />
              <Row t={t} last label="Date" value={r.date} />
              <Btn t={t} label="↓ Download PDF" style={{ marginTop: 8, padding: '9px 0', fontSize: 12 }} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── MY SAVINGS ────────────────────────────────────────────────────
export function MySavings({ t }) {
  const [view, setView] = useState('chart');
  return (
    <div style={{ padding: '16px 16px 32px' }}>
      {/* P02 hero */}
      <div style={{ background: '#1B5E20', borderRadius: 16, padding: '16px 18px', marginBottom: 16 }}>
        <div style={{ color: '#A5D6A7', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Project 02 — Ongoing</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[['Installments', '96,000 BDT', '#fff'], ['Down payment', '13,000 BDT', '#FDD835']].map(([l, v, c]) => (
            <div key={l} style={{ background: 'rgba(255,255,255,.1)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ color: '#A5D6A7', fontSize: 11, fontWeight: 600 }}>{l}</div>
              <div style={{ color: c, fontSize: 18, fontWeight: 900, marginTop: 3 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: 'rgba(200,164,0,.2)', borderRadius: 10, padding: '10px 14px', marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '0.5px solid rgba(200,164,0,.4)' }}>
          <span style={{ color: '#FDD835', fontSize: 14, fontWeight: 800 }}>Total contributed</span>
          <span style={{ color: '#FDD835', fontSize: 22, fontWeight: 900 }}>1,09,000</span>
        </div>
      </div>

      {/* DP Progress */}
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Down payment progress" right={<span style={{ color: '#E65100', fontWeight: 800, fontSize: 13 }}>54%</span>} />
        <ProgressBar t={t} value={54} max={100} color="#E65100" height={10} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 6, fontWeight: 600, color: t.textSec }}>
          <span>Paid: 13,000</span><span>Target: 24,000 (2 shares)</span>
        </div>
      </Card>

      {/* Chart */}
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Monthly payments — 2025" right={
          <div style={{ display: 'flex', background: t.surface2, borderRadius: 8, padding: 2, border: `0.5px solid ${t.border}` }}>
            {['chart', 'table'].map(v => (<div key={v} onClick={() => setView(v)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: view === v ? t.primary : 'transparent', color: view === v ? '#fff' : t.textSec, textTransform: 'capitalize' }}>{v}</div>))}
          </div>
        } />
        {view === 'chart' ? (
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={CHART_DATA} barSize={26}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: t.textHint, fontFamily: 'Nunito' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v) => [`${(v/1000).toFixed(0)}K BDT`, 'Amount']} contentStyle={{ background: t.surface, border: `0.5px solid ${t.border}`, borderRadius: 8, fontSize: 11, fontFamily: 'Nunito' }} />
              <Bar dataKey="amount" fill={t.primary2} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          CHART_DATA.map(d => <Row key={d.month} t={t} label={d.month + ' 2025'} value={d.amount > 0 ? `${d.amount.toLocaleString()} BDT` : '—'} valueColor={d.amount > 0 ? t.primary : t.textHint} />)
        )}
      </Card>

      <Btn t={t} label="📄 Export my statement" />
    </div>
  );
}

// ── APP SETTINGS ──────────────────────────────────────────────────
export function AppSettings({ t, isDark, onToggleDark }) {
  const [notifs, setNotifs] = useState({ payments: true, deadlines: true, announcements: true, milestones: false });
  const [biometric, setBiometric] = useState(true);

  return (
    <div style={{ padding: '16px 16px 32px' }}>
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Appearance" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
          {[['☀️', 'Light', !isDark], ['🌙', 'Dark', isDark]].map(([icon, lbl, active]) => (
            <div key={lbl} onClick={onToggleDark} style={{ borderRadius: 12, padding: '12px 8px', textAlign: 'center', background: active ? t.primaryLight : t.surface2, border: `${active ? 1.5 : 0.5}px solid ${active ? t.primary : t.border}`, cursor: 'pointer' }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontSize: 12, fontWeight: active ? 800 : 600, color: active ? t.primary : t.textSec }}>{lbl}</div>
              {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.primary, margin: '4px auto 0' }} />}
            </div>
          ))}
          <div style={{ borderRadius: 12, padding: '12px 8px', textAlign: 'center', background: t.surface2, border: `0.5px solid ${t.border}`, cursor: 'pointer' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>📱</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.textSec }}>System</div>
          </div>
        </div>
        <div style={{ background: t.primaryLight, borderRadius: 8, padding: '8px 12px', border: `0.5px solid ${t.borderLight}`, fontSize: 12, color: t.primary, fontWeight: 600 }}>
          {isDark ? '🌙 Dark — #040507 + #81C784 · Nunito' : '☀️ Light — #1B5E20 green · Nunito'}
        </div>
      </Card>

      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Notifications" />
        {Object.entries(notifs).map(([key, val]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `0.5px solid ${t.border}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</div>
            <Toggle t={t} value={val} onChange={v => setNotifs(n => ({ ...n, [key]: v }))} />
          </div>
        ))}
      </Card>

      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Security" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>Biometric login</div>
            <div style={{ fontSize: 12, color: t.textHint, fontWeight: 500, marginTop: 2 }}>Fingerprint / Face ID</div>
          </div>
          <Toggle t={t} value={biometric} onChange={setBiometric} />
        </div>
      </Card>

      <Card t={t}>
        <SectionTitle t={t} label="About" />
        <Row t={t} label="App version" value="v1.0.0" />
        <Row t={t} label="Developer" value="Md Shahriar Nazim Rasel" />
        <Row t={t} label="Organization" value="SB Group" />
        <Row t={t} label="Font" value="Nunito (Google Fonts)" />
        <Row t={t} last label="Copyright" value="© 2024 SB Group" />
      </Card>
    </div>
  );
}
